import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { Box, Button, Grid, MenuItem, TextField } from "@mui/material";
import Modal from "@mui/material/Modal";
import SaveAlt from "@mui/icons-material/SaveAlt";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import "./AdminComponents.scss";
import { getNurses, updateNurse, deleteNurse, NURSE_ROLES } from "../api/nurseMasterApi";
import {
  dataGridSx,
  modalBoxSx,
  confirmButtonSx,
  cancelButtonSx,
} from "./bedMasterStyles";

/**
 * Nurse List — the grid half of the Nurse Management screen. Same shape as
 * DoctorList / BedTypeList: a DataGrid with per-row Edit and Delete, both
 * driven by the `nurse_id` UUID (not the numeric `id`).
 */

const SALUTATIONS = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Prof"];

const GENDERS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "T", label: "Transgender" },
];

const splitName = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  return {
    first_name: parts[0] || "",
    last_name: parts.slice(1).join(" "),
  };
};

// The list returns the display label ("Ms."), edit_nurse/ stores the code
// ("Ms") — strip the trailing dot so the select matches an option.
const salutationCode = (value = "") => String(value).replace(/\.$/, "");

const dateForInput = (value) => (value ? String(value).split("T")[0] : "");

const roleValue = (row = {}) => {
  const raw = String(row.role || row.nurse_type || "").trim().toLowerCase();
  const match = NURSE_ROLES.find((r) => r.value === raw);
  return match ? match.value : raw;
};

const roleLabel = (value) => {
  const match = NURSE_ROLES.find((r) => r.value === String(value || "").toLowerCase());
  return match ? match.label : value || "-";
};

function NurseList({ refreshList }) {
  const [nurses, setNurses] = useState([]);

  const [nurseToDelete, setNurseToDelete] = useState(null);
  const [editingNurse, setEditingNurse] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editDob, setEditDob] = useState(null);
  const [editDateJoined, setEditDateJoined] = useState(null);

  const fetchNurses = () => {
    getNurses()
      .then((list) =>
        setNurses(
          list.map((item) => ({
            nurse_id: item.nurse_id,
            username: item.username ?? "",
            name: item.name ?? "",
            salutation: item.salutation ?? "",
            hospital: item.hospital ?? "",
            email: item.email ?? "",
            phone_no: item.phone_no ?? "",
            registration_no: item.registration_no ?? "",
            specialization: item.specialization ?? "",
            role: roleValue(item),
            qualifications: item.qualifications ?? "",
            gender: item.gender ?? "",
            dob: dateForInput(item.dob),
            city: item.city ?? "",
            state: item.state ?? "",
            district: item.district ?? "",
            date_joined: dateForInput(item.date_joined),
            active: item.active !== undefined ? item.active : true,
          }))
        )
      )
      .catch((err) => {
        console.error("getNurses:", err);
        toast.error("Could not load nurses");
      });
  };

  useEffect(() => {
    fetchNurses();
  }, [refreshList]);

  const handleEditOpen = (row) => {
    const parts = splitName(row.name);
    setEditingNurse({
      ...row,
      salutation: salutationCode(row.salutation),
      first_name: parts.first_name,
      last_name: parts.last_name,
    });
    // Convert YYYY-MM-DD strings to Date objects for DatePicker
    if (row.dob) {
      setEditDob(new Date(row.dob));
    } else {
      setEditDob(null);
    }
    if (row.date_joined) {
      setEditDateJoined(new Date(row.date_joined));
    } else {
      setEditDateJoined(null);
    }
  };

  const handleConfirmDelete = () => {
    const nurseId = nurseToDelete.nurse_id;
    deleteNurse(nurseId)
      .then((res) => {
        if (res?.status === "Success") {
          toast.success(res?.message || "Nurse Deleted Successfully");
          // Hard delete — drop the row locally instead of re-listing.
          setNurses((rows) => rows.filter((row) => row.nurse_id !== nurseId));
        } else {
          toast.error(res?.message || "Could not delete nurse");
        }
      })
      .catch((err) => {
        console.error("deleteNurse:", err);
        toast.error(err?.response?.data?.message || "Could not delete nurse");
      })
      .finally(() => setNurseToDelete(null));
  };

  const handleEditSubmit = () => {
    if (!String(editingNurse.first_name || "").trim()) {
      toast.error("First name is required");
      return;
    }
    setIsSaving(true);
    // facility_id is deliberately not sent — omitted keys keep their current
    // value, so the nurse stays in their facility. `name` is left out too, so
    // the backend recomposes it from first_name / last_name and the linked
    // login user stays in step.
    const payload = {
      nurse_id: editingNurse.nurse_id,
      first_name: editingNurse.first_name,
      last_name: editingNurse.last_name,
      salutation: editingNurse.salutation,
      email: editingNurse.email,
      phone_no: editingNurse.phone_no,
      registration_no: editingNurse.registration_no,
      specialization: editingNurse.specialization,
      role: editingNurse.role,
      qualifications: editingNurse.qualifications,
      gender: editingNurse.gender,
      city: editingNurse.city,
      state: editingNurse.state,
      district: editingNurse.district,
      active: editingNurse.active,
    };
    // Only send date fields if they have values (Date objects)
    if (editDob) {
      payload.dob = editDob.toISOString().split("T")[0];
    }
    if (editDateJoined) {
      payload.date_joined = editDateJoined.toISOString().split("T")[0];
    }
    updateNurse(payload)
      .then((res) => {
        if (res?.status === "Success") {
          toast.success(res?.message || "Nurse Updated Successfully");
          setEditingNurse(null);
          setEditDob(null);
          setEditDateJoined(null);
          fetchNurses();
        } else {
          toast.error(res?.message || "Could not update nurse");
        }
      })
      .catch((err) => {
        console.error("updateNurse:", err);
        toast.error(err?.response?.data?.message || "Could not update nurse");
      })
      .finally(() => setIsSaving(false));
  };

  const dashIfEmpty = (params) => params?.value || "-";

  const columns = [
    {
      field: "name",
      headerName: "Name",
      width: 180,
      valueFormatter: dashIfEmpty,
    },
    {
      field: "username",
      headerName: "User Name",
      width: 130,
      valueFormatter: dashIfEmpty,
    },
    {
      field: "role",
      headerName: "Role",
      width: 180,
      valueFormatter: (params) => roleLabel(params?.value),
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
      valueFormatter: dashIfEmpty,
    },
    {
      field: "phone_no",
      headerName: "Phone No",
      width: 130,
      valueFormatter: dashIfEmpty,
    },
    {
      field: "specialization",
      headerName: "Specialization",
      width: 160,
      valueFormatter: dashIfEmpty,
    },
    {
      field: "registration_no",
      headerName: "Registration No",
      width: 150,
      valueFormatter: dashIfEmpty,
    },
    {
      field: "hospital",
      headerName: "Facility",
      width: 130,
      valueFormatter: dashIfEmpty,
    },
    {
      field: "active",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <div
          style={{ color: params.value ? "green" : "red", fontWeight: "bold" }}
        >
          {params.value ? "Active" : "Inactive"}
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <button
            onClick={() => handleEditOpen(params.row)}
            style={{ color: "blue", fontWeight: "bold", marginRight: "8px" }}
          >
            Edit
          </button>
          <button
            onClick={() => setNurseToDelete(params.row)}
            style={{ color: "red", fontWeight: "bold" }}
          >
            Delete
          </button>
        </>
      ),
    },
  ];

  const generatePdf = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "Name",
          "Role",
          "Email",
          "Phone No",
          "Specialization",
          "Facility",
          "Status",
        ],
      ],
      body: nurses.map((n) => [
        n.name || "-",
        roleLabel(n.role),
        n.email || "-",
        n.phone_no || "-",
        n.specialization || "-",
        n.hospital || "-",
        n.active ? "Active" : "Inactive",
      ]),
    });
    doc.save("Nurse_List.pdf");
  };

  const generateCsvData = () =>
    nurses.map((n) => ({
      Name: n.name || "-",
      "User Name": n.username || "-",
      Role: roleLabel(n.role),
      Email: n.email || "-",
      "Phone No": n.phone_no || "-",
      Specialization: n.specialization || "-",
      "Registration No": n.registration_no || "-",
      Facility: n.hospital || "-",
      Status: n.active ? "Active" : "Inactive",
    }));

  // Every plain edit field is the same controlled TextField, only the key
  // differs — the three selects below are spelled out instead.
  const editField = (key, label, extra = {}) => (
    <Grid item xs={12} sm={6}>
      <TextField
        label={label}
        value={editingNurse[key] ?? ""}
        onChange={(e) =>
          setEditingNurse({ ...editingNurse, [key]: e.target.value })
        }
        fullWidth
        InputLabelProps={extra.type === "date" ? { shrink: true } : undefined}
        {...extra}
      />
    </Grid>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: "auto",
        width: "100%",
        maxWidth: "1200px",
        padding: "1rem",
      }}
    >
      <div style={{ width: "100%" }}>
        <h4 style={{ textAlign: "center" }}>Nurse List</h4>

        <div className="display-flex">
          <DataGrid
            rows={nurses}
            columns={columns}
            getRowId={(row) => row.nurse_id}
            components={{
              Toolbar: () => (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={8}
                    sx={{ display: "flex", gap: 1 }}
                  >
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarDensitySelector />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: { xs: "center", sm: "flex-end" },
                    }}
                  >
                    <Button
                      variant="text"
                      color="primary"
                      onClick={generatePdf}
                      startIcon={<SaveAlt />}
                    >
                      Download PDF
                    </Button>
                    <CSVLink
                      data={generateCsvData()}
                      filename={"nurse_list.csv"}
                      target="_blank"
                    >
                      <Button
                        variant="text"
                        color="primary"
                        startIcon={<SaveAlt />}
                      >
                        Download CSV
                      </Button>
                    </CSVLink>
                  </Grid>
                </Grid>
              ),
            }}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
            }}
            pageSizeOptions={[5, 10]}
            getRowClassName={() => "custom-row"}
            sx={dataGridSx}
          />
        </div>
      </div>

      <Modal
        open={!!nurseToDelete}
        onClose={() => setNurseToDelete(null)}
        style={{
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
        aria-labelledby="confirm-delete-nurse-modal"
      >
        <Box sx={modalBoxSx}>
          <h3
            id="confirm-delete-nurse-modal"
            style={{ marginTop: 0, fontSize: "32px" }}
          >
            Confirm Deletion
          </h3>
          {/* Hard delete — it also removes the nurse's login user. */}
          <p style={{ fontSize: "20px" }}>
            Are you sure you want to delete nurse{" "}
            <b>{nurseToDelete?.name || nurseToDelete?.nurse_id}</b>? This also
            removes their login user and cannot be undone.
          </p>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "16px",
              mt: "10%",
            }}
          >
            <Button onClick={() => setNurseToDelete(null)} sx={cancelButtonSx}>
              CANCEL
            </Button>
            <Button onClick={handleConfirmDelete} sx={confirmButtonSx}>
              CONFIRM DELETION
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={!!editingNurse}
        onClose={() => {
          setEditingNurse(null);
          setEditDob(null);
          setEditDateJoined(null);
        }}
        style={{
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
        aria-labelledby="edit-nurse-modal"
      >
        <Box sx={modalBoxSx}>
          <h3 id="edit-nurse-modal" style={{ marginTop: 0 }}>
            Edit Nurse
          </h3>
          {editingNurse && (
            <form onSubmit={(e) => e.preventDefault()}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Salutation"
                    value={editingNurse.salutation ?? ""}
                    onChange={(e) =>
                      setEditingNurse({
                        ...editingNurse,
                        salutation: e.target.value,
                      })
                    }
                    fullWidth
                  >
                    {SALUTATIONS.map((title) => (
                      <MenuItem key={title} value={title}>
                        {title}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {editField("first_name", "First Name")}
                {editField("last_name", "Last Name")}
                {editField("email", "Email", { type: "email" })}
                {editField("phone_no", "Phone No")}
                {editField("registration_no", "Registration No")}
                {editField("specialization", "Specialization")}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Role"
                    value={editingNurse.role ?? ""}
                    onChange={(e) =>
                      setEditingNurse({
                        ...editingNurse,
                        role: e.target.value,
                      })
                    }
                    fullWidth
                  >
                    {NURSE_ROLES.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {editField("qualifications", "Qualifications")}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Gender"
                    value={editingNurse.gender ?? ""}
                    onChange={(e) =>
                      setEditingNurse({
                        ...editingNurse,
                        gender: e.target.value,
                      })
                    }
                    fullWidth
                  >
                    {GENDERS.map((g) => (
                      <MenuItem key={g.value} value={g.value}>
                        {g.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <label style={{ display: "block", marginBottom: "8px" }}>
                    Date of Birth
                  </label>
                  <DatePicker
                    selected={editDob}
                    onChange={(date) => setEditDob(date)}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-mm-yyyy"
                    className="form-control"
                    style={{ width: "100%" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <label style={{ display: "block", marginBottom: "8px" }}>
                    Date Joined
                  </label>
                  <DatePicker
                    selected={editDateJoined}
                    onChange={(date) => setEditDateJoined(date)}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-mm-yyyy"
                    className="form-control"
                    style={{ width: "100%" }}
                  />
                </Grid>
                {editField("city", "City")}
                {editField("district", "District")}
                {editField("state", "State")}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Status"
                    value={editingNurse.active ? "true" : "false"}
                    onChange={(e) =>
                      setEditingNurse({
                        ...editingNurse,
                        active: e.target.value === "true",
                      })
                    }
                    fullWidth
                  >
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              <Button
                type="submit"
                disabled={isSaving}
                onClick={handleEditSubmit}
                variant="contained"
                style={{
                  backgroundColor: "#213360",
                  color: "#fff",
                  border: "2px solid #213360",
                  borderRadius: "20px",
                  marginTop: "20px",
                }}
              >
                Save Changes
              </Button>
            </form>
          )}
        </Box>
      </Modal>
    </div>
  );
}

export default NurseList;
